import * as React from "react";
import { useHistory } from "react-router-dom";

import { titles } from "../../../constants/titles";
import {
  clearErrorMessage,
  clearSuccessMessage,
  Context,
  bulkUpsertStudents,
  bulkDeleteStudents,
  BULK_UPSERT_STUDENTS_SUCCESS_MESSAGE,
  BULK_DELETE_STUDENTS_SUCCESS_MESSAGE,
  studentsPath,
  fetchClasses,
  fetchStudents,
  fetchPlans,
  FETCH_STUDENTS_SUCCESS_MESSAGE,
  FETCH_STUDENTS_SUCCESS,
  FETCH_STUDENTS_FAILURE,
  FETCH_CLASSES_SUCCESS_MESSAGE,
  RESET_STUDENTS_SUCCESS_MESSAGE,
} from "../../../managerStore";
import { Breadcrumb } from "../../../molecules/Breadcrumb";
import StudentSpreadSheet from "../../../templates/manager/StudentSpreadSheet";
import { IStudentForRegister } from "../../../state";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import useSchoolStatus from "../../../hooks/manager/useSchoolStatus";
import Toast from "../../../molecules/Toast";

const BulkEditStudents = () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const [isSelectMode, setIsSelectMode] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);
  const [filterByClass, setFilterByClass] = React.useState("");
  const [filterByYear, setFilterByYear] = React.useState("");
  const [fetched, setFetched] = React.useState({
    students: false,
    classes: false,
  });
  const history = useHistory();
  const { currentYear } = useCurrentYear();
  const { isCramSchool } = useSchoolStatus();

  React.useEffect(() => {
    if (state.success_message === BULK_UPSERT_STUDENTS_SUCCESS_MESSAGE) {
      dispatch(clearSuccessMessage());
      history.push(studentsPath);
    }
    if (state.success_message === BULK_DELETE_STUDENTS_SUCCESS_MESSAGE) {
      Toast.success(state.success_message);
      setIsSelectMode(false);
      dispatch(clearSuccessMessage());
    }
    if (state.success_message === FETCH_STUDENTS_SUCCESS_MESSAGE) {
      setTimeout(() => {
        setFetched(s => ({ ...s, students: true }));
      }, 100);
    }
    if (state.success_message === FETCH_CLASSES_SUCCESS_MESSAGE) {
      setTimeout(() => {
        setFetched(s => ({ ...s, classes: true }));
      }, 100);
    }
  }, [history, dispatch, state.success_message]);

  const bulkUpsertStudentsFunc = React.useCallback(
    (students: IStudentForRegister[]) => {
      dispatch(peep(bulkUpsertStudents(students)));
    },
    [dispatch, peep],
  );

  const bulkDeleteStudentsFunc = React.useCallback(
    (student_ids: number[]) => {
      dispatch(peep(bulkDeleteStudents(student_ids)));
    },
    [dispatch, peep],
  );

  const refetchStudents = React.useCallback(() => {
    setIsResetting(true);
    dispatch(peep(fetchClasses(currentYear)));
    dispatch(
      peep(
        fetchStudents({
          class_id: filterByClass,
          entrance_year: filterByYear,
          include_can_destroy: true,
        }),
      ),
    );
  }, [dispatch, peep, filterByClass, filterByYear, currentYear]);

  React.useEffect(() => {
    dispatch(fetchClasses(currentYear));
    return () => dispatch(clearErrorMessage());
  }, [dispatch, currentYear]);

  React.useEffect(() => {
    dispatch(
      fetchStudents({
        class_id: filterByClass,
        entrance_year: filterByYear,
        include_can_destroy: true,
      }),
    );
  }, [filterByClass, filterByYear, dispatch]);

  React.useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  React.useEffect(() => {
    if (!isResetting) return;
    if (state.lastAction === FETCH_STUDENTS_SUCCESS) {
      Toast.success(RESET_STUDENTS_SUCCESS_MESSAGE);
      dispatch(clearSuccessMessage());
      setIsResetting(false);
    }
    if (state.lastAction === FETCH_STUDENTS_FAILURE) {
      setIsResetting(false);
    }
  }, [dispatch, isResetting, state.lastAction]);

  return (
    <div>
      <Breadcrumb
        links={[
          {
            title: isCramSchool ? titles.cramSchoolStudents : titles.students,
            to: studentsPath,
          },
        ]}
        currentTitle={titles.bulkEditStudents}
      />
      {fetched.classes && fetched.students && (
        <StudentSpreadSheet
          title={titles.bulkEditStudents}
          calling={calling}
          error={state.error}
          students={state.students}
          classes={state.classes}
          school={state.manager.school!}
          isNextYear={false}
          save={bulkUpsertStudentsFunc}
          deleteStudents={bulkDeleteStudentsFunc}
          failedStudentIds={state.bulk_delete_failed_ids}
          isSelectMode={isSelectMode}
          onChangeSelectMode={setIsSelectMode}
          reset={refetchStudents}
          isResetting={isResetting}
          onFilterByClass={setFilterByClass}
          onFilterByYear={setFilterByYear}
          plans={state.plans}
        />
      )}
    </div>
  );
};

export default BulkEditStudents;
